import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Facebook, Instagram, Twitter, Music, Linkedin, Youtube } from "lucide-react";
import { fetchSocialLinkByType } from "../../redux/Slices/contactInfoSlice";
import { useAppDispatch } from "../../redux";

interface SocialMediaLinksProps {
  className?: string;
  iconSize?: string;
  showLabels?: boolean;
  containerSize?: string;
}

const SocialMediaLinks: React.FC<SocialMediaLinksProps> = ({ 
  className = "flex justify-center space-x-2 sm:space-x-3 pt-4 border-t border-gray-100",
  iconSize = "h-3 w-3 sm:h-4 sm:w-4",
  showLabels = false,
  containerSize = "w-6 h-6 sm:w-6 sm:h-6"
}) => {
  const dispatch = useAppDispatch();
  const { socialLinks } = useSelector((state: any) => state.contactInfos);
  const { current: socialLinkData, loading, error } = socialLinks;

  useEffect(() => {
    // Only fetch if we don't already have data and we're not currently loading
    if (!socialLinkData && !loading) {
      dispatch(fetchSocialLinkByType(1));
    }
  }, [dispatch, socialLinkData, loading]);

  // Get URLs from API response or use defaults
  const getSocialUrl = (platform: string) => {
    if (socialLinkData && socialLinkData[platform]) {
      return socialLinkData[platform];
    }
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="flex justify-center space-x-2 sm:space-x-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="w-6 h-6 sm:w-6 sm:h-6 bg-gray-200 rounded-full animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <a
        href={getSocialUrl("facebook")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-blue-600 rounded-full hover:opacity-90 transition-opacity`}
        title="Facebook"
      >
        <Facebook className={`${iconSize} text-white`} />
      </a>
      <a
        href={getSocialUrl("instagram")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 rounded-full hover:opacity-90 transition-opacity`}
        title="Instagram"
      >
        <Instagram className={`${iconSize} text-white`} />
      </a>
      <a
        href={getSocialUrl("tiktok")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-black rounded-full hover:opacity-90 transition-opacity`}
        title="TikTok"
      >
        <Music className={`${iconSize} text-white`} />
      </a>
      <a
        href={getSocialUrl("twitter")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-blue-400 rounded-full hover:opacity-90 transition-opacity`}
        title="Twitter"
      >
        <Twitter className={`${iconSize} text-white`} />
      </a>
      <a
        href={getSocialUrl("youtube")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-red-600 rounded-full hover:opacity-90 transition-opacity`}
        title="YouTube"
      >
        <Youtube className={`${iconSize} text-white`} />
      </a>
      <a
        href={getSocialUrl("linkedin")}
        target="_blank"
        rel="noopener noreferrer"
        className={`${containerSize} flex items-center justify-center bg-blue-800 rounded-full hover:opacity-90 transition-opacity`}
        title="LinkedIn"
      >
        <Linkedin className={`${iconSize} text-white`} />
      </a>
    </div>
  );
};

export default SocialMediaLinks;
